import { useRef, useState } from 'react'
import type { SubmitHandler } from 'react-hook-form'
import { AiPromptModal } from '@/components/commons/ai-prompt-modal'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { Container } from '@/components/ui/container'

import { Settings01Icon } from '@/components/ui/icons'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Scrollbar } from '@/components/ui/scrollbar'
import { ExpShellMerchInputForm } from '@/features/experience-vetting/components/exp-shell-merch-input-form'
import { ExpShellMerchOutputForm } from '@/features/experience-vetting/components/exp-shell-merch-output-form'
import type {
	ExperienceMerchandisingInputSchema,
	ExperienceShellMerchOutputSchema
} from '@/features/experience-vetting/form-schemas'
import { OpsVettingHero } from '@/features/ops-vetting/components/ops-vetting-atoms'
import { useMerchCopyAi } from '@/features/ops-vetting/hooks/use-merch-copy-ai'
import { EXPERIENCE_SHELL_PROMPT } from '@/features/ops-vetting/prompts/experience-shell-prompt'
import { useTranslation } from '@/translations/i18n'

export const ExpShellCopyTraining = () => {
	const { t } = useTranslation()
	const {
		aiResponse,
		isLoading: isAiLoading,
		onMerchCopyAiCall,
		resetAiResponse
	} = useMerchCopyAi<ExperienceShellMerchOutputSchema>()
	//submission refs
	const finalSubmitRef = useRef<HTMLButtonElement>(null)
	const aiSubmissionRef = useRef<HTMLButtonElement>(null)
	const [currentPrompt, setCurrentPrompt] = useState<string>(
		EXPERIENCE_SHELL_PROMPT
	)

	const handleAiSubmission: SubmitHandler<
		ExperienceMerchandisingInputSchema
	> = (data) => {
		resetAiResponse()
		onMerchCopyAiCall(data, currentPrompt)
	}

	return (
		<div className='grid h-full grid-cols-[minmax(0,1fr)] grid-rows-[minmax(0,1fr)] overflow-hidden'>
			<ScrollArea scrollbar={<Scrollbar />}>
				<Container
					className='grid grid-cols-[minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)]'
					fullWidth
				>
					<OpsVettingHero
						align='center'
						fullWidth
						justify='space-between'
					>
						<h3>Exp shell Copy Training</h3>
						<div className='flex gap-2'>
							<AiPromptModal
								currentPrompt={currentPrompt}
								isLoading={isAiLoading}
								onPromptChange={(newPrompt) =>
									setCurrentPrompt(newPrompt)
								}
								promptType='exp_shell'
								trigger={
									<ButtonLegacy
										disabled={isAiLoading}
										type='button'
										variant='tertiary'
									>
										<Settings01Icon />
										{t(
											'vetting:merch.commons.customize_prompt'
										)}
									</ButtonLegacy>
								}
							/>
						</div>
					</OpsVettingHero>

					<div className='mt-6 mb-10 flex w-full overflow-hidden rounded-2xl border border-neutral-200'>
						<ExpShellMerchInputForm
							aiResponse={aiResponse}
							aiSubmissionRef={aiSubmissionRef}
							defaultValues={{}}
							handleAiSubmissionCb={handleAiSubmission}
							isReadOnly={false}
							isTraining={true}
						/>
						<ExpShellMerchOutputForm
							defaultValues={aiResponse}
							finalSubmitRef={finalSubmitRef}
							isHostEdits={false}
							isLoading={isAiLoading}
							isReadOnly={false}
							isTraining={true}
						/>
					</div>
				</Container>
			</ScrollArea>
		</div>
	)
}

ExpShellCopyTraining.displayName = 'ExpShellCopyTraining'
